import React, { useEffect, useLayoutEffect, useRef } from "react";
import { MonitorMeta, MonitorRow, SortOrder } from "./types";
import { GridColumn } from "./columns";
import { useManualGridResize } from "./useManualGridResize";
import { useColumnWidths } from "./useColumnWidths";
import { useFillAvailableHeight, BASELINE_VIEWPORT_RATIO } from "./useFillAvailableHeight";

// HHMCOVID-6327 — floor/ceiling for the manually-resizable grid body.
// ABSOLUTE_MAX_HEIGHT must stay one constant regardless of interaction
// state, since mutating it mid-drag would race the browser's native
// resize gesture and cause a visible jump. FALLBACK_HEIGHT shares
// useFillAvailableHeight's baseline ratio so the two can't drift apart.
const FALLBACK_HEIGHT = `${Math.round(BASELINE_VIEWPORT_RATIO * 100)}vh`;
const ABSOLUTE_MAX_HEIGHT = "90vh";
const MIN_HEIGHT = 150;
// HHMCOVID-6327 — auto-fit height transition. A window/monitor-size change
// (AC6) can swing the fill height by hundreds of pixels in one jump, unlike
// AC4's fixed, small DETAILS_FOCUS_SHRINK, so this needs to be long enough to
// read as a smooth resize rather than a snap.
const HEIGHT_TRANSITION_MS = 450;
const HEIGHT_TRANSITION = `height ${HEIGHT_TRANSITION_MS}ms ease`;
// Scroll-into-view retry (see the effect below) must land after
// HEIGHT_TRANSITION finishes moving the row, so the row's final settled
// position is what gets scrolled to.
const FOCUS_SCROLL_RETRY_MS = HEIGHT_TRANSITION_MS + 50;

// HHMCOVID-6283 — per-column slack covering cell borders. HHMCOVID-6343 applied it
// to the <col> elements too, not just the tables' `min-width`: carrying it in only
// one place left the shrink-to-fit tables wider than the <colgroup> summed to, and
// `table-layout: fixed` redistributed that surplus across every column. Columns
// rendered ~5% oversized, so a drag tracked the cursor at ~95% and shrinking one
// column widened all the others. In both places, the surplus is exactly 0.
const COLUMN_WIDTH_PADDING = 6;

// HHMCOVID-6327 AC4 — fixed pixel amount the auto-fit height shrinks by while
// a patient is focused, so the Patient Details panel is visible without
// scrolling. Only applies to the auto-fit height, not a manual resize (AC1).
const DETAILS_FOCUS_SHRINK = 300;

// HHMCOVID-6327 AC4/AC5 — the grid's auto-fit height, used only when
// manualHeight == null. `fillHeight` is `null` only transiently, before
// useFillAvailableHeight's one-time measurement completes. The shrink is a
// fixed, unconditional subtraction whenever a patient is focused — never a
// fresh DOM measurement — so a row click and an AC3 restore-on-return land
// on the exact same height (see useFillAvailableHeight's own note on why
// the baseline measurement can't be trusted to already reflect the panel).
function computeAutoFitHeight(fillHeight: number | null, hasFocusedPatient: boolean): string | number {
  if (fillHeight == null) {
    return FALLBACK_HEIGHT;
  }
  return Math.max(fillHeight - (hasFocusedPatient ? DETAILS_FOCUS_SHRINK : 0), MIN_HEIGHT);
}

interface GridProps {
  rows: MonitorRow[];
  meta: MonitorMeta;
  // HHMCOVID-5632 (BR-019) — the full ordered column list, built once in
  // MonitorDashboardApp and shared with the "Customize Columns" dialog, plus the
  // per-user set of hidden column keys.
  columns: GridColumn[];
  hiddenColumns: string[];
  selectedIds: number[];
  focusedPatientId: number | null;
  sortname: string;
  sortorder: SortOrder;
  loading: boolean;
  // HHMCOVID-6327 AC5 — true once the first data fetch has completed (success
  // or failure). Distinct from `loading`, which starts `false` before the
  // very first fetch even begins — see useFillAvailableHeight.
  hasLoadedOnce: boolean;
  onSelectRow: (id: number, options?: { multi?: boolean; range?: boolean }) => void;
  onSort: (sortname: string, initialOrder?: SortOrder) => void;
  onOpenPatient: (patientId: number) => void;
}

export default function Grid(props: GridProps) {
  const { rows, meta, columns, hiddenColumns, selectedIds, focusedPatientId, sortname, sortorder, loading, hasLoadedOnce, onSelectRow, onSort, onOpenPatient } = props;
  const hDivRef = useRef<HTMLDivElement | null>(null);
  const bDivRef = useRef<HTMLDivElement | null>(null);
  const hDivBoxRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // HHMCOVID-6343 — drag-to-resize columns, restoring 2.10's flexigrid behaviour.
  // Owns the width overrides used at every width site below; see useColumnWidths
  // for why the grid doesn't resize live.
  const columnWidths = useColumnWidths(wrapperRef, meta.react_state_key);

  // HHMCOVID-6327 AC1/AC2 — manual vertical resize of the grid body, with the
  // result persisted/restored across navigation. See useManualGridResize for
  // how a genuine user drag is distinguished from incidental size changes
  // (window resize, zoom, content reflow).
  const manualHeight = useManualGridResize(bDivRef, meta.react_state_key);

  // HHMCOVID-6327 AC2/AC5/AC6 — fill of leftover viewport space, persisted
  // per session and kept live on window resize; see useFillAvailableHeight.
  const { fillHeight } = useFillAvailableHeight(
    bDivRef,
    hasLoadedOnce,
    manualHeight != null,
    meta.react_state_key
  );
  const autoFitHeight = computeAutoFitHeight(fillHeight, focusedPatientId != null);

  // HHMCOVID-5632 (BR-019) — drop the user's hidden columns before rendering.
  // Filtering the built array keeps hidden columns out of the DOM entirely
  // (immediate show/hide, no refetch); unhidden columns reappear in their
  // original registry position.
  const visibleColumns = columns.filter((column) => !hiddenColumns.includes(column.key));

  // Declared ahead of the effects below, which take it as their width signal — a
  // const referenced from a dep array must already be initialised by then.
  const tableMinWidth = visibleColumns.reduce((sum, col) => sum + columnWidths.widthOf(col) + COLUMN_WIDTH_PADDING, 0);

  // HHMCOVID-6327 — keeps the focused row visible: re-runs on a height
  // change (AC4 shrink, AC5 landing, AC6 live resize) that shifts scroll
  // position, or once `rows` populates (AC3 can restore a focus before
  // rows finish loading, so the row doesn't exist yet on the first
  // attempt). Scrolls immediately and again once HEIGHT_TRANSITION settles.
  useEffect(() => {
    if (focusedPatientId == null) {
      return;
    }
    const bDiv = bDivRef.current;
    if (!bDiv) {
      return;
    }
    const scrollFocusedRowIntoView = () => {
      const row = bDiv.querySelector(`[data-row-id="${focusedPatientId}"]`);
      row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    };
    scrollFocusedRowIntoView();
    const timer = setTimeout(scrollFocusedRowIntoView, FOCUS_SCROLL_RETRY_MS);
    return () => clearTimeout(timer);
  }, [focusedPatientId, manualHeight, autoFitHeight, rows]);

  useEffect(() => {
    const hDiv = hDivRef.current;
    const bDiv = bDivRef.current;
    if (!hDiv || !bDiv) {
      return;
    }
    const syncScroll = () => {
      hDiv.scrollLeft = bDiv.scrollLeft;
    };
    bDiv.addEventListener("scroll", syncScroll, { passive: true });
    return () => bDiv.removeEventListener("scroll", syncScroll);
  }, []);

  // HHMCOVID-6343 — the sync above needs bDiv's `scroll` event, which a width
  // change doesn't necessarily fire: widening extends hDiv's scroll range without
  // scrolling, and narrowing clamps each element's scrollLeft independently.
  // Legacy re-copied it at the end of a column drag (flexigrid.js:406).
  useLayoutEffect(() => {
    const hDiv = hDivRef.current;
    const bDiv = bDivRef.current;
    if (!hDiv || !bDiv) {
      return;
    }
    hDiv.scrollLeft = bDiv.scrollLeft;
  }, [tableMinWidth]);

  // HHMCOVID-6283 — flexigrid's static `padding-right: 40px` guess at
  // bDiv's scrollbar width is wrong for real widths, and wrong (should be
  // 0) once row count is too small to overflow — copying scrollLeft onto
  // hDiv then drifts further out of sync the further right a user scrolls.
  // Measures the real width and applies it inline, scoped to this component.
  useLayoutEffect(() => {
    const bDiv = bDivRef.current;
    const hDivBox = hDivBoxRef.current;
    if (!bDiv || !hDivBox) {
      return;
    }
    const recomputeScrollbarPadding = () => {
      hDivBox.style.paddingRight = `${bDiv.offsetWidth - bDiv.clientWidth}px`;
    };
    recomputeScrollbarPadding();
    window.addEventListener("resize", recomputeScrollbarPadding);
    return () => window.removeEventListener("resize", recomputeScrollbarPadding);
    // HHMCOVID-6327 — manualHeight is included because resizing the grid can
    // add/remove the vertical scrollbar (e.g. growing it tall enough that all
    // rows now fit), which changes the real scrollbar width this recomputes.
    // HHMCOVID-6343 — tableMinWidth matters in both directions: narrowing wraps
    // text onto more lines, growing rows until a vertical scrollbar appears;
    // widening adds a horizontal one, cutting clientHeight so the vertical can
    // appear too. Either way a stale paddingRight brings the drift above back.
  }, [rows.length, loading, hiddenColumns.length, manualHeight, autoFitHeight, tableMinWidth]);

  const strings = meta.grid_strings || ({} as any);

  const renderHeader = (column: GridColumn, index: number) => {
    const isActiveSort = column.sortname && sortname === column.sortname;
    const sortClass = isActiveSort ? (sortorder === "asc" ? "sasc" : "sdesc") : "";
    // flexigrid draws the sort-arrow on the inner <div>, not the <th>
    // (flexigrid.css.scss:470-478) — sortClass goes on both: the <div> so
    // the arrow renders, and the <th> for the `sorted` border-bottom style
    // (flexigrid.css.scss:428-447) and E2E locator compatibility
    // (HHMCOVID-5612). Hover uses a CSS fallback for legacy's `.thOver` JS toggle, not replicated here.
    const thClasses: string[] = [];
    if (column.sortname) thClasses.push("sortable");
    if (isActiveSort) thClasses.push("sorted", sortClass);
    const thClassName = thClasses.join(" ");
    return (
      <th
        key={column.key}
        className={thClassName}
        abbr={column.sortname || ""}
        data-axis={`col${index}`}
        // HHMCOVID-6343 — containing block for the resize grip below.
        style={{ position: "relative" }}
        onClick={() => {
          // HHMCOVID-6343 — the guarantee that a resize never sorts; the grip's own
          // stopPropagation can't cover a short drag released over the label, which
          // resolves its click to this <th>. See beginResize.
          if (columnWidths.consumeSortSuppression()) {
            return;
          }
          if (column.sortname) {
            onSort(column.sortname, column.initialSortOrder);
          }
        }}
      >
        <div
          className={sortClass}
          style={{
            textAlign: column.align,
            width: `${columnWidths.widthOf(column)}px`,
            whiteSpace: "normal",
            wordWrap: "break-word",
            overflowWrap: "break-word"
          }}
        >
          {column.label}
        </div>
        {/* HHMCOVID-6343 — drag hit strip on the column's right border. MUST stay a
            <span>: as a <div> it would match `th.sortable:hover div`
            (flexigrid.css.scss:449), whose `cursor: pointer` would override
            col-resize on every sortable header — the very symptom this ticket reports. */}
        <span
          className="monitor-col-resize-grip"
          data-column-key={column.key}
          onMouseDown={(event) => columnWidths.beginResize(column, event)}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
        />
      </th>
    );
  };

  const renderCell = (column: GridColumn, row: MonitorRow) => (
    <td key={`${row.id}-${column.key}`}>
      <div
        style={{
          textAlign: column.align,
          width: `${columnWidths.widthOf(column)}px`,
          whiteSpace: "normal",
          wordWrap: "break-word",
          overflowWrap: "break-word"
        }}
      >
        {column.render(row)}
      </div>
    </td>
  );

  // HHMCOVID-6283 — shared between both tables so their columns are guaranteed
  // pixel-identical, since each `<table>` otherwise sizes its own columns
  // independently (one header row vs. many data rows) under the default
  // `table-layout: auto`, which is what let the header and body drift apart.
  //
  // HHMCOVID-6343 — these carry COLUMN_WIDTH_PADDING too, so they sum to exactly
  // `tableMinWidth` and `table-layout: fixed` has no surplus to redistribute. The
  // inner cell <div>s stay on the bare model width.
  const colGroup = (
    <colgroup>
      {visibleColumns.map((column) => (
        <col key={column.key} style={{ width: `${columnWidths.widthOf(column) + COLUMN_WIDTH_PADDING}px` }} />
      ))}
    </colgroup>
  );

  return (
    <div className="flexigrid" ref={wrapperRef} style={{ width: "100%" }}>
      <div
        className="hDiv"
        ref={hDivRef}
        style={{ overflow: "hidden", background: "#fff" }}
      >
        <div className="hDivBox" ref={hDivBoxRef}>
          <table cellSpacing={0} cellPadding={0} style={{ minWidth: `${tableMinWidth}px`, tableLayout: "fixed" }}>
            {colGroup}
            <thead>
              <tr className="hDr">
                {visibleColumns.map(renderHeader)}
              </tr>
            </thead>
          </table>
        </div>
      </div>

      <div
        className="bDiv"
        ref={bDivRef}
        style={
          // ABSOLUTE_MAX_HEIGHT never varies with interaction state (see
          // above). `transition` only applies to the auto-fit branch —
          // animating a live `resize: vertical` drag would make dragging
          // feel laggy, chasing the transition instead of the mouse.
          // `boxSizing: "border-box"` makes this element's sizing model
          // explicit and deterministic — see useManualGridResize's matching
          // getBoundingClientRect()-based measurement.
          manualHeight != null
            ? { height: manualHeight, minHeight: MIN_HEIGHT, maxHeight: ABSOLUTE_MAX_HEIGHT, overflow: "auto", resize: "vertical", boxSizing: "border-box" }
            : { height: autoFitHeight, minHeight: MIN_HEIGHT, maxHeight: ABSOLUTE_MAX_HEIGHT, overflow: "auto", resize: "vertical", boxSizing: "border-box", transition: HEIGHT_TRANSITION }
        }
      >
        <table cellSpacing={0} cellPadding={0} style={{ minWidth: `${tableMinWidth}px`, tableLayout: "fixed" }}>
          {colGroup}
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length}>
                  <div>
                    {loading
                      ? strings.procmsg || "Processing, please wait..."
                      : strings.nomsg || "No Patients to display"}
                  </div>
                </td>
              </tr>
            )}
            {rows.map((row, index) => {
              const selected = selectedIds.includes(row.id);
              const focused = focusedPatientId === row.id;
              const classNames = [
                index % 2 === 1 ? "erow" : "",
                selected || focused ? "trSelected" : ""
              ].filter((name) => name.length > 0).join(" ");
              return (
                <tr
                  key={row.id}
                  data-row-id={row.id}
                  className={classNames}
                  onClick={(event) => onSelectRow(row.id, { multi: event.ctrlKey || event.metaKey, range: event.shiftKey })}
                  onDoubleClick={() => onOpenPatient(row.id)}
                >
                  {visibleColumns.map((column) => renderCell(column, row))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* HHMCOVID-6343 — full-height line following the cursor during a column drag,
          standing in for legacy's `.dragging` grip. Deliberately has NO style prop:
          useColumnWidths moves it imperatively, so a drag costs zero re-renders and
          React can't clobber those writes. Last child so it paints over both tables. */}
      <div className="monitor-col-resize-guide" ref={columnWidths.guideRef} />
    </div>
  );
}
